import { css } from 'lit';

export const styles = css`
	:host {
		--_sds-radio-font-family: var(--sds-radio-font-family, var(--sds-typography-font-family-roboto));
		--_sds-radio-disabled-opacity: var(--sds-radio-disabled-opacity, var(--sds-color-states-disabled-opacity));
		--_sds-radio-block-gap: var(--sds-radio-block-gap, var(--sds-spacing-4xs));
		--_sds-radio-inline-gap: var(--sds-radio-inline-gap, var(--sds-spacing-3xs));

		--_sds-radio-label-font-size: var(--sds-radio-label-font-size, var(--sds-components-font-size-content-md));
		--_sds-radio-label-font-weight: var(--sds-radio-label-font-weight, var(--sds-typography-body-md-desktop-font-weight));
		--_sds-radio-label-line-height: var(--sds-radio-label-line-height, var(--sds-components-line-height-tall-content-md));
		--_sds-radio-label-text-color: var(--sds-radio-label-text-color, var(--sds-color-text-default, var(--sds-color-neutral-text-default)));

		--_sds-radio-help-text-color: var(--sds-radio-help-text-color, var(--sds-color-text-subtle, var(--sds-color-neutral-text-subtle)));
		--_sds-radio-help-text-font-size: var(--sds-radio-help-text-font-size, var(--sds-components-font-size-content-md));
		--_sds-radio-help-text-font-weight: var(--sds-radio-help-text-font-weight, var(--sds-typography-body-md-desktop-font-weight));
		--_sds-radio-help-text-line-height: var(--sds-radio-help-text-line-height, var(--sds-components-line-height-tall-content-md));

		--_sds-radio-input-size: var(--sds-radio-input-size, var(--sds-components-icon-md));
		--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-surface-default, var(--sds-color-neutral-surface-default)));
		--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-border-default, var(--sds-color-neutral-border-default)));
		--_sds-radio-input-border-width: var(--sds-radio-input-border-width, var(--sds-border-md));
		--_sds-radio-input-dot-color: var(--sds-radio-input-dot-color, transparent);
		--_sds-radio-input-dot-size: var(--sds-radio-input-dot-size, 4.5px);

		--_sds-radio-grid-template-areas: var(--sds-radio-grid-template-area, 'input content');
		--_sds-radio-label-grid-area: var(--sds-radio-label-grid-area, content);
		--_sds-radio-input-grid-area: var(--sds-radio-input-grid-area, input);
		--_sds-radio-help-text-grid-area: var(--sds-radio-help-text-grid-area, content);

		display: inline-flex;
		flex: 0 0 auto;
	}

	.sds-radio {
		display: grid;
		font-family: var(--_sds-radio-font-family);
		gap: var(--_sds-radio-block-gap) var(--_sds-radio-inline-gap);
		grid-template-areas: var(--_sds-radio-grid-template-areas);
		grid-template-columns: auto 1fr;
		width: fit-content;

		&:has(:disabled) {
			cursor: default;
			pointer-events: none;
			opacity: var(--_sds-radio-disabled-opacity);
		}

		&:has(:focus-visible) {
			outline: 3px solid var(--sds-color-states-focus-outer);
			outline-offset: 6px;
			border-radius: var(--sds-radius-sm);
		}
	}

	.sds-radio--error {
		--_sds-radio-label-text-color: var(--sds-radio-label-text-color, var(--sds-color-text-default, var(--sds-color-alert-danger-text-default)));
	}

	.sds-radio__help-text {
		grid-column: var(--_sds-radio-help-text-grid-area);
		margin: 0;
		color: var(--_sds-radio-help-text-color);
		font-size: var(--_sds-radio-help-text-font-size);
		font-weight: var(--_sds-radio-help-text-font-weight);
		line-height: var(--_sds-radio-help-text-line-height);
		max-width: var(--_sds-radio-max-line-width);
	}

	.sds-radio__label {
		cursor: pointer;
		color: var(--_sds-radio-label-text-color);
		display: inline-block;
		font-size: var(--_sds-radio-label-font-size);
		font-weight: var(--_sds-radio-label-font-weight);
		line-height: var(--_sds-radio-label-line-height);

		grid-column: var(--_sds-radio-label-grid-area);
	}

	.sds-radio__input {
		appearance: none;
		background-color: var(--_sds-radio-input-background-color);
		background-image: radial-gradient(circle closest-side, currentcolor var(--_sds-radio-input-dot-size), transparent 0);
		color: var(--_sds-radio-input-dot-color);
		cursor: pointer;
		width: var(--_sds-radio-input-size);
		height: var(--_sds-radio-input-size);
		aspect-ratio: 1;
		border: var(--_sds-radio-input-border-width) solid var(--_sds-radio-input-border-color);
		border-radius: 50%;
		margin: 0;
		position: relative;

		grid-column: var(--_sds-radio-input-grid-area);
		grid-row: 1;

		&[readonly] {
			--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-surface-tinted, var(--sds-color-neutral-surface-tinted)));
			--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-border-subtle, var(--sds-color-neutral-border-subtle)));
			cursor: default;
			pointer-events: none;

			& + .sds-radio__label {
				cursor: default;
				pointer-events: none;
			}
		}

		&[checked] {
			--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
			--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
			--_sds-radio-input-dot-color: var(--sds-radio-input-dot-color, var(--sds-color-action-contrast-default, var(--sds-color-main-action-contrast-default)));

			&[readonly] {
				--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-surface-tinted, var(--sds-color-neutral-surface-tinted)));
				--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-border-subtle, var(--sds-color-neutral-border-subtle)));
				--_sds-radio-input-dot-color: var(--sds-radio-input-dot-color, var(--sds-color-action-default, var(--sds-color-neutral-action-default)));
				cursor: default;
				pointer-events: none;
			}
		}

		&:hover {
			--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-surface-default, var(--sds-color-neutral-surface-default)));
			--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-action-hover, var(--sds-color-main-action-hover)));

			&[checked] {
				--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-action-hover, var(--sds-color-main-action-hover)));
				--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-action-hover, var(--sds-color-main-action-hover)));
				--_sds-radio-input-dot-color: var(--sds-radio-input-dot-color, var(--sds-color-surface-default, var(--sds-color-neutral-surface-default)));
			}
		}

		&:active:not([readonly], [checked]) {
			--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-surface-default, var(--sds-color-neutral-surface-default)));
			--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-action-active, var(--sds-color-main-action-active)));

			&[checked] {
				--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-action-active, var(--sds-color-main-action-active)));
				--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-action-active, var(--sds-color-main-action-active)));
				--_sds-radio-input-dot-color: var(--sds-radio-input-dot-color, var(--sds-color-surface-default, var(--sds-color-neutral-surface-default)));
			}
		}

		&:focus-visible {
			outline: none;
		}

		&.sds-radio__input--error {
			--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-alert-danger-border-default));

			&[checked] {
				--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-alert-danger-action-default));
				--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-alert-danger-action-default));
				--_sds-radio-input-dot-color: var(--sds-radio-input-dot-color, var(--sds-color-alert-danger-action-contrast-default));
			}

			&:hover {
				--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-surface-default, var(--sds-color-neutral-surface-default)));
				--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-alert-danger-action-hover));

				&[checked] {
					--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-alert-danger-action-hover));
					--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-alert-danger-action-hover));
					--_sds-radio-input-dot-color: var(--sds-radio-input-dot-color, var(--sds-color-surface-default, var(--sds-color-neutral-surface-default)));
				}
			}

			&:active:not([readonly], [checked]) {
				--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-surface-default, var(--sds-color-neutral-surface-default)));
				--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-alert-danger-action-active));

				&[checked] {
					--_sds-radio-input-background-color: var(--sds-radio-input-background-color, var(--sds-color-alert-danger-action-active));
					--_sds-radio-input-border-color: var(--sds-radio-input-border-color, var(--sds-color-alert-danger-action-active));
					--_sds-radio-input-dot-color: var(--sds-radio-input-dot-color, var(--sds-color-surface-default, var(--sds-color-neutral-surface-default)));
				}
			}
		}
	}

	:host([size='sm']) {
		--_sds-radio-label-font-size: var(--sds-radio-label-font-size, var(--sds-components-font-size-content-sm));
		--_sds-radio-label-line-height: var(--sds-radio-label-line-height, var(--sds-components-line-height-tall-content-sm));
		--_sds-radio-input-size: var(--sds-radio-input-size, var(--sds-components-icon-sm));
		--_sds-radio-input-dot-size: var(--sds-radio-input-dot-size, 3.5px);

		.sds-radio__label {
			margin-block-start: 1px;
		}
	}

	:host([size='lg']) {
		--_sds-radio-label-font-size: var(--sds-radio-label-font-size, var(--sds-components-font-size-content-lg));
		--_sds-radio-label-line-height: var(--sds-radio-label-line-height, var(--sds-components-line-height-tall-content-lg));
		--_sds-radio-input-size: var(--sds-radio-input-size, var(--sds-components-icon-lg));
		--_sds-radio-input-dot-size: var(--sds-radio-input-dot-size, 4.5px);

		.sds-radio__label {
			margin-block-start: -1px;
		}
	}
`;
